import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { CompanyItem, UserGroupItem } from '@nirvana/shared/dtos/types/policies/common';
import { API_TYPE_GZ, WEB_COMPONENT_EDIT_USER_GROUP_CONTAINER } from '../../../../../../../../webcomponents.constants';
import { customElement, query } from 'lit/decorators.js';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../ui/gzChipsGeneratorInput';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { USER_GROUPS_TYPE } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';
import { InputWidthValues } from '../../../../../../../../ui/gzInput';
import { CompanySelectorWebComponent } from '../../../../../../../../companySelector';
import { GzHardCodedFormField } from '../../../../../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { AvailableCompaniesSource, GetCompaniesRequest } from '@nirvana/shared/dtos/types/dashboards/gz-console';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  FILTER_CHANGED,
  TARGET_FILTERS_TYPES,
} from '../../../../../../../../xdrSearchApp/xdrLiveSearchQueryPanel/types/constants';
import {
  CompanyApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { styles } from './gzExchangeProtectionUserGroupsGrid/view/styles';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { GzInputErrorWebComponent } from '../../../../../../../../ui/gzForm/inputError';
import { toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { EmailValidator } from '../../../../../../../../ui/gzForm/validators/email.validator';

@customElement(WEB_COMPONENT_EDIT_USER_GROUP_CONTAINER)
export class GzEditUserGroupContainerWebComponent extends GzPolicyFullPageWindowFormWebComponent<UserGroupItem> {
  private userGroupItem?: UserGroupItem;
  private targetCompanies: CompanyItem[];

  dependencies = {
    translateFn: translateFn,
    api: {
      type: API_TYPE_GZ,
      clients: {
        Company: {
          getAvailableCompanies: (
            PolicyDependenciesContainer.getInstance().resolveDependency('Company') as CompanyApiClients
          ).getAvailableCompanies,
        },
      },
    },
    getActiveCompany: (PolicyDependenciesContainer.getInstance().resolveDependency('Company') as CompanyApiClients)
      .getActiveCompany,
  };

  static get styles() {
    return styles;
  }

  constructor(userGroupItem?: UserGroupItem) {
    super();
    this.userGroupItem = userGroupItem;
  }

  @query('#name-form-field')
  name: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#description-form-field')
  description: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#emails-form-field')
  emails: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#company-selector')
  companySelector: CompanySelectorWebComponent;
  @query('#company-input-error')
  companyInputError: GzInputErrorWebComponent;
  @query('#company-form-hidden')
  companyId: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#company-selector-field')
  companyField: HTMLElement;

  protected createFormGroup(): void {
    this.name.Validators = [RequiredValidator(translateFn('Required field'))];
    this.emails.Validators = [RequiredValidator(translateFn('Required field'))];
    this.companyId.Validators = this.isCloudPartner() ? [RequiredValidator(translateFn('Required field'))] : [];

    this.companyId.valid$.subscribe(valid => {
      if (!this.companyId.touched) {
        return;
      }

      this.companyInputError.Error = valid ? null : translateFn('Required field');
      const companyDropdown =
        this.companySelector.shadowRoot?.querySelector<GzDropdownWebComponent>('#companySelectorDropdown');
      if (companyDropdown) {
        companyDropdown.invalid = !valid;
      }
    });

    this.formGroup = new FormGroup({
      name: this.name,
      description: this.description,
      companyId: this.companyId,
      guid: new GzHardCodedFormField<string>(this.userGroupItem ? this.userGroupItem.specifics.guid : ''),
      emails: this.emails,
    });
  }

  protected setFormGroupValue(): void {
    this.formGroup.value = {
      name: this.userGroupItem?.name ?? '',
      description: this.userGroupItem?.specifics.description ?? '',
      companyId: this.userGroupItem?.specifics.company.id ?? '',
      emails: this.userGroupItem?.specifics.users ?? '',
    };

    if (this.isCloudPartner()) {
      // make company field appear
      toggleVisibility(this.companyField, true);

      // build company selector
      let companyId = '';
      let companyName = '';
      if (this.userGroupItem) {
        companyId = this.userGroupItem?.specifics.company.id;
        companyName = this.userGroupItem?.specifics.company.name ?? '';
      }
      this.buildCompanySelectorDropdown(companyId, companyName);

      (
        this.companySelector.shadowRoot?.querySelector('#companySelectorDropdown') as GzDropdownWebComponent
      ).addEventListener('close', (event: CustomEvent) => {
        this.companyId.validate(true);
      });
    }
  }

  protected getConvertedFormValue() {
    const formValue = this.formGroup.value;
    const returnValue: UserGroupItem = {
      _id: this.userGroupItem === undefined ? undefined : this.userGroupItem._id,
      name: formValue.name as string,
      type: USER_GROUPS_TYPE.CUSTOM,
      specifics: {
        description: formValue.description as string,
        company: {
          id: formValue.companyId as string,
        },
        guid: '',
        users: formValue.emails as string[],
      },
    };

    return returnValue;
  }

  protected renderContent(): TemplateResult {
    return html`
      <gz-form-field
        id="name-form-field"
        class="label-mw-250 text-input-form-field"
        label="${translateFn('Name')}"
        required
      >
        <gz-input
          placeholder="${translateFn('Enter the group name')}"
          gz-form-input
          inputWidth="${InputWidthValues.LARGE}"
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field
        id="description-form-field"
        class="label-mw-250 text-input-form-field"
        label="${translateFn('Description')}"
      >
        <gz-input
          placeholder="${translateFn('Enter a description')}"
          inputWidth="${InputWidthValues.LARGE}"
          gz-form-input
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <div class="company-selector-field hidden" id="company-selector-field">
        <span class="company-selector-text">${translateFn('Company{end}', { end: '*:' })}</span>
        <div style="display:flex; flex-direction: column;">
          <gz-company-selector
            id="company-selector"
            class="company-selector"
            placeholder="${translateFn('Select company')}"
          ></gz-company-selector>
          <gz-input-error id="company-input-error"></gz-input-error>
        </div>
      </div>
      <gz-form-field id="emails-form-field" required>
        <gz-chips-generator-input
          id="chips-generator-input"
          .validators=${[EmailValidator(translateFn('Invalid email address'))]}
          hasFloatingLabel
          .strings="${{
            placeholder: translateFn('Enter email addresses'),
            invalidItemErrorMessage: translateFn('Invalid entry'),
            clearListButtonText: translateFn('Clear list'),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Remove'),
            label: translateFn('Email addresses'),
            deleteWindowTitle: translateFn('Remove email addresses?'),
            hintText: translateFn('Use a comma to separate multiple email addresses.'),
            deleteWindowContentText: translateFn(
              'You are about to remove all email addresses from the list. This action cannot be undone.'
            ),
            notUniqueErrorMessage: translateFn('The email address already exists.'),
          }}"
          style="--chip-list-width: 490px;
          --form-field-with-floating-max-width: 490px;
          --form-field-max-width: 490px;"
          gz-form-input
        >
        </gz-chips-generator-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
      <gz-form-field id="company-form-hidden" class="hidden"> <gz-input gz-form-input></gz-input></gz-form-field>
    `;
  }

  async buildCompanySelectorDropdown(companyId: string, companyName: string) {
    // started component here as it needs extra time to be initialized
    this.companySelector.injectDependencies(this.dependencies);
    this.companySelector.start().catch(console.error);

    this.companySelector.propertiesSet = false;

    this.companySelector.setSelectedOption({
      title: companyName,
      value: companyId,
    });

    this.companySelector.companySelectorParams = {
      options: {
        source: AvailableCompaniesSource.FROM_EXCHANGE,
        countRecords: false,
      },
    };

    if (!this.targetCompanies) {
      const response = await this.dependencies.api.clients.Company.getAvailableCompanies({
        params: {
          options: {
            source: AvailableCompaniesSource.FROM_EXCHANGE,
            countRecords: false,
          },
        },
      })
        .toPromise()
        .catch(console.error);

      this.targetCompanies = response ? response.data : [];
    }

    this.companySelector.selectedTarget = {
      companyId: companyId,
      companyName: companyName,
    };

    this.companySelector.label = translateFn('Company');
    this.companySelector.availableCompanies = this.targetCompanies;
    this.companySelector.onCompanyClicked = this.onCompanyClicked.bind(this);
    this.companySelector.propertiesSet = true;
  }

  async onCompanyClicked(clickedCompany: Target) {
    await this.setSelectedCompany(clickedCompany);
    this.companyId.value = clickedCompany.companyId;
    this.companyId.touched = true;
  }

  public async setSelectedCompany(company: Target) {
    this.dispatchEvent(
      new CustomEvent(FILTER_CHANGED, {
        detail: {
          companyId: company.companyId,
          companyName: company.companyName,
          target: TARGET_FILTERS_TYPES.COMPANY,
        },
        bubbles: true,
      })
    );
  }

  isCloudPartner(): boolean {
    return Application.Main.product.isCloud() && Application.Main.company.isPartner();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [WEB_COMPONENT_EDIT_USER_GROUP_CONTAINER]: GzEditUserGroupContainerWebComponent;
  }
}
